<template>
  <!-- vehicle details card -->
  <v-card flat class="pa-5 bg-pwt_snow">
    <VehicleBookingBand v-if="vehicle && vehicle.directusId" :vehicle="vehicle" />
    <!-- not connected vehicle notif -->
    <NoLiveDataNotif
      v-if="vehicle && vehicle.orakle_update_error"
      :orakle_update_error="vehicle.orakle_update_error"
    />
    <!-- vehicle details card -->
    <v-card class="py-4 pl-md-12 rounded-lg bg-pwt_snow mb-10" flat>
      <v-row class="flex-column flex-md-row">
        <v-col md="3">
          <!-- image section -->
          <v-skeleton-loader type="image" width="100%" height="140px" v-if="!vehicle" />
          <v-img
            v-else
            contain
            :src="imageSrc()"
            aspect-ratio="1"
            :width="mdAndUp ? '100%' : '40%'"
            :height="mdAndUp ? '100%' : 'auto'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <v-col md="9">
          <!-- details section -->
          <div v-if="vehicle">
            <v-card-title class="d-flex flex-nowrap align-center">
              {{
                vehicle.vehicleType === VehicleType.GENERIC_VEHICLE
                  ? 'Generic EV'
                  : (vehicle.licensePlate ?? 'unrecorded license plate')
              }}
              <v-spacer></v-spacer>
              <CustomSolidButton narrow>
                Book {{ vehicle.licensePlate ? `'${vehicle.licensePlate}'` : '' }}
              </CustomSolidButton>

              <v-menu location="start" offset="-45 -50">
                <template v-slot:activator="{ props }">
                  <v-btn
                    flat
                    color="transparent"
                    v-bind="props"
                    icon="mdi-dots-horizontal"
                    class="text-pwt_mid_grey"
                  />
                </template>
                <v-list>
                  <v-list-item
                    title="Set as default trip vehicle"
                    @click="isSelected ? handleSelectOther() : handleSelect()"
                  />
                  <v-list-item title="Edit details" @click="toEditVehiclePage" />
                  <v-list-item title="Make a copy" />
                  <v-list-item
                    title="View on map"
                    v-if="liveDataEnabled"
                    @click="viewOnMap"
                    :disabled="!!vehicle.orakle_update_error || !vehicle.directusId"
                  />

                  <v-list-item title="Delete" class="text-pwt_red" />
                </v-list>
              </v-menu>
            </v-card-title>
            <v-card-subtitle class="font-weight-medium">
              {{ vehicle.name ?? 'unnamed vehicle' }}
            </v-card-subtitle>
            <v-card-text class="pr-12">
              <p class="mb-0">
                {{ vehicle.licensePlate ?? 'unrecorded license plate' }}
              </p>
              <p v-if="vehicle.evModel">
                {{ vehicle.evModel.name }}
              </p>
              <template v-if="liveDataEnabled">
                <NotConnectedBatteryDisplay v-if="!vehicle.stateOfCharge" />
                <BatteryDisplay
                  v-else
                  :battery="vehicle.stateOfCharge"
                  :style="mdAndUp ? 'max-width: 50%' : ''"
                />
              </template>
            </v-card-text>
          </div>
          <div v-else>
            <v-row no-gutters class="mb-4">
              <v-skeleton-loader type="heading" width="70%" />
              <v-skeleton-loader type="heading" width="30%" />
            </v-row>
            <v-skeleton-loader type="text" width="40%" />
            <v-skeleton-loader type="text" width="70%" class="mb-4" />
            <v-skeleton-loader type="heading" width="100%" />
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Details section -->
    <v-card class="py-4 px-8 rounded-lg mb-10 bg-pwt_snow" elevation="1">
      <v-card-title> Details section </v-card-title>
      <v-card-text>
        <div class="pa-2 bg-background d-flex rounded mb-3">
          <v-icon :icon="fuelIcon" class="mr-3" color="pwt_azure_80" />
          {{ fuelType }}
        </div>
        <div
          v-if="fuelType === 'Electric' || fuelType === 'Hybrid' || fuelType === 'Plug in hybrid'"
          class="pa-2 bg-background d-flex rounded mb-3"
        >
          <v-icon icon="mdi-battery-30" class="mr-3" color="pwt_azure_80" />
          {{ batterySize }} kWh
        </div>
        <div class="pa-2 bg-background d-flex rounded mb-3">
          <v-icon icon="custom:outlinedWeight" class="mr-3" color="pwt_azure_80" />
          {{ vehicleWeight }} kg
        </div>
      </v-card-text>
      <v-row no-gutters>
        <v-col v-if="fuelType === 'Electric' || fuelType === 'Plug in hybrid'" cols="12" md="6">
          <v-card-title> Charging speed </v-card-title>
          <v-card-text>
            <div class="pa-2 bg-background d-flex rounded mb-3">
              <v-icon icon="custom:outlinedEvStation" class="mr-3" color="pwt_azure_80" />
              {{ acRating }} kWh AC (slow) charging speed
            </div>
            <div class="pa-2 bg-background d-flex rounded mb-3">
              <v-icon icon="custom:outlinedEvStation" class="mr-3" color="pwt_azure_80" />
              {{ dcRating }} kWh DC (fast) charging speed
            </div>
          </v-card-text>
        </v-col>
        <v-col cols="12" :md="fuelType === 'Electric' || fuelType === 'Plug in hybrid' ? 6 : 12">
          <v-card-title> Additional load capacity </v-card-title>
          <v-card-text>
            <div class="pa-2 bg-background d-flex rounded mb-3">
              <v-icon icon="custom:rvHookup" class="mr-3" color="pwt_azure_80" />
              Can carry upto {{ additionalWeight }}kg
            </div>
            <div class="pa-2 bg-background d-flex rounded mb-3">
              <v-icon icon="mdi-package-variant-closed" class="mr-3" color="pwt_azure_80" />
              {{ bootSize }}L boot
            </div>
          </v-card-text>
        </v-col>
      </v-row>
      <v-card-title v-if="fuelType === 'Electric' || fuelType === 'Plug in hybrid'" class="d-flex">
        <span class="mr-3"> Compatible connectors </span>
        <StyledToolTip v-if="connectorSettingsTooltip" :data="connectorSettingsTooltip" />
      </v-card-title>
      <v-card-text class="mb-3">
        <v-row no-gutters class="ga-3">
          <div
            v-for="connector in selectedConnectors"
            class="d-flex align-center pa-2 rounded flex-nowrap"
            style="border: 1px solid #dadada; min-width: 20%"
          >
            <img :src="connector.iconURL ?? fallbackImgSrc()" width="26" height="26" class="mr-3" />
            <span class="pr-3">
              {{ connector.displayName + (connector.format === 'SOCKET' ? ' cable' : '') }}
            </span>
          </div>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Efficiency & adjustments section -->
    <v-expansion-panels bg-color="pwt_snow" class="mb-5">
      <v-expansion-panel color="pwt_snow">
        <v-expansion-panel-title class="pt-8 pl-12 text-h6">
          Efficiency & adjustments
        </v-expansion-panel-title>
        <v-expansion-panel-text color="pwt_snow" class="py-4 px-12">
          <!-- range adjustment -->
          <v-row v-if="fuelType === 'Electric'" class="mb-5 flex-column flex-md-row">
            <v-col md="4" align-self="center">
              <p>
                Range Adjustment
                <StyledToolTip v-if="sohToolTipContent" :data="sohToolTipContent" />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              <v-row no-gutters class="ga-2 flex-nowrap" align="center">
                <v-col cols="3">
                  <span> {{ SoH }}% or {{ rangeKm }}km </span>
                </v-col>
                <v-col cols="8">
                  <BarGauge :percentage="SoH" dense />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Regenerative breaking efficiency slider -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                Regenerative braking
                <StyledToolTip
                  v-if="regenerativeBrakingEfficiencyTooltip"
                  :data="regenerativeBrakingEfficiencyTooltip"
                />
              </p>
            </v-col>

            <v-col md="8" align-self="center">
              <v-row no-gutters class="ga-2 flex-nowrap" align="center">
                <v-col cols="3">
                  <span> {{ Math.round(regenerativeBraking) }}% </span>
                </v-col>
                <v-col cols="8">
                  <BarGauge :percentage="regenerativeBraking" dense />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <!-- Power train efficiency slider -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                vehicle's powertrain
                <StyledToolTip
                  v-if="powerTrainEfficiencyTooltip"
                  :data="powerTrainEfficiencyTooltip"
                />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              <v-row no-gutters class="ga-2 flex-nowrap" align="center">
                <v-col cols="3">
                  <span> {{ Math.round(powerTrain) }}% </span>
                </v-col>
                <v-col cols="8">
                  <BarGauge :percentage="powerTrain" dense />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Aerodynamics slider -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                Aerodynamics (drag)
                <StyledToolTip v-if="dragTooltip" :data="dragTooltip" />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              {{ (aerodynamics / 100).toFixed(5) }}
            </v-col>
          </v-row>
          <!-- Tyre dynamics slider -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                Tyre dynamics (grip)
                <StyledToolTip v-if="gripTooltip" :data="gripTooltip" />
              </p>
            </v-col>
            <v-col md="8" align-self="center">
              {{ (tyreDynamics / 1000).toString() }}
            </v-col>
          </v-row>

          <!-- Energy used when stopped in kW -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                Energy used when stopped
                <StyledToolTip
                  v-if="energyUsedWhenStoppedTooltip"
                  :data="energyUsedWhenStoppedTooltip"
                />
              </p>
            </v-col>
            <v-col md="8" align-self="center"> {{ energyUsedWhenStopped }} kW </v-col>
          </v-row>
          <!-- Energy used when moving in kW -->
          <v-row class="flex-column flex-md-row mb-5">
            <v-col md="4" align-self="center">
              <p>
                Energy used when moving
                <StyledToolTip
                  v-if="energyUsedWhenMovingTooltip"
                  :data="energyUsedWhenMovingTooltip"
                />
              </p>
            </v-col>
            <v-col md="8" align-self="center"> {{ energyUsedWhenMoving }} kW </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script lang="ts" setup>
import EVModel from '@/classes/vehicle_classes/evModel'
import Vehicle from '@/classes/vehicle_classes/vehicle'
import CustomSolidButton from '@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue'
import BarGauge from '@/components/ui-elements/displays/BarGauge.vue'
import {
  type ConnectorDetailsData,
  connectorDetailsDataMap,
  type ConnectorDetailsIconData,
  getAllSocketedConnectors,
  getAllTetheredConnectors,
} from '@/data/connectorDetailsData'
import { useDashboardNavStore } from '@/store/index.ts'
import {
  ActionTypes,
  MainDialogContent,
  ManagedContentNames,
  MutationTypes,
  type State,
} from '@/store/store_types'
import type { ConnectorFormat, ConnectorType } from '@/types/charger_Db_types'
import type { DirectusUserSelectedPlug } from '@/types/directus-types'
import type { FuelType } from '@/types/sheared_local_types'
import { VehicleType } from '@/types/vehicle_specific_types.ts'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import BatteryDisplay from '../../ui-elements/displays/BatteryDisplay.vue'
import NotConnectedBatteryDisplay from '../../ui-elements/displays/NotConnectedBatteryDisplay.vue'
import StyledToolTip from '../../ui-elements/displays/StyledToolTip.vue'
import NoLiveDataNotif from '../NoLiveDataNotif.vue'
import VehicleBookingBand from '../VehicleBookingBand.vue'

const dashboardNavStore = useDashboardNavStore()
const toEditVehiclePage = () => {
  dashboardNavStore.setDashboardDialogContent('vehicle-edit-details')
}

interface LocalConnectorData {
  iconURL?: string
  displayName: string
  standard: ConnectorType
  format: ConnectorFormat
}

const { vehicle, isSelected = false } = defineProps<{
  vehicle?: Vehicle | undefined
  isSelected?: boolean
}>()

const { smAndDown, mdAndUp } = useDisplay()

const store = useStore<State>()
// control refs
const dirty = ref(false)
const loading = ref(true)
const liveDataEnabled = ref<boolean>(import.meta.env.VITE_FEATURE_FLAG_ENABLE_ORAKLE === 'true')
// weights refs
const vehicleWeight = ref(1000)
const additionalWeight = ref(0)
const bootSize = ref(0)
// fuel refs
const fuelType = ref<FuelType>('Electric')
const fuelIcon = computed<string>(() => {
  switch (fuelType.value) {
    case 'Electric':
      return 'custom:offlineBolt'
    case 'Hybrid':
    case 'Plug in hybrid':
      return 'custom:waterEc'
    case 'Hydrogen':
      return 'custom:hydrogen'
    case 'Diesel':
    case 'Petrol':
    case 'Other':
    default:
      return 'custom:waterDrop'
  }
})
const batterySize = ref(25)
const SoH = ref(100)
const rangeKm = computed(() => Math.round(vehicle ? vehicle.calcMaxRange() / 1000 : 0))
// charging refs
const acRating = ref(10)
const dcRating = ref(50)
// energy refs
const energyUsedWhenStopped = ref(0)
const energyUsedWhenMoving = ref(0)
// physics profile refs
const aerodynamics = ref(50)
const tyreDynamics = ref(50)
const regenerativeBraking = ref(50)
const powerTrain = ref(50)
// connectors refs
const selectedConnectors = ref<LocalConnectorData[]>([])
// tooltip content
const sohToolTipContent = computed(() =>
  store.state.managedContent.find((content) => content.Name === ManagedContentNames.SoHToolTip),
)
const vehicleWeightTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details vehicle weight tooltip',
  ),
)
const additionalWeightTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details additional weight tooltip',
  ),
)
const fuelTypeTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'vehicle details fuel type tooltip',
  ),
)
const batterySizeTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model battery size tooltip',
  ),
)
const acChargingSpeedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model ac charging speed tooltip',
  ),
)
const dcChargingSpeedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model dc charging speed tooltip',
  ),
)
const energyUsedWhenStoppedTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model energy used when stopped tooltip',
  ),
)
const energyUsedWhenMovingTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model additional energy used when moving tooltip',
  ),
)
const dragTooltip = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'ev model drag tooltip'),
)
const gripTooltip = computed(() =>
  store.state.managedContent.find((content) => content.Description === 'ev model grip tooltip'),
)
const regenerativeBrakingEfficiencyTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model regenerative braking efficiency tooltip',
  ),
)
const powerTrainEfficiencyTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model power train efficiency tooltip',
  ),
)
const connectorSettingsTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'ev model connector settings tooltip',
  ),
)
// cables and connectors

/**
 * Orders the given array of LocalConnectorData objects based on whether they are selected or not.
 *
 * @param {LocalConnectorData[]} connectors - The array of LocalConnectorData objects to order.
 * @return {LocalConnectorData[]} The ordered array of LocalConnectorData objects.
 */
function orderConnectors(connectors: LocalConnectorData[]): LocalConnectorData[] {
  const returnArray: LocalConnectorData[] = []
  connectors.forEach((connector) => {
    if (connectorIsSelected(connector)) {
      returnArray.unshift(connector)
    } else {
      returnArray.push(connector)
    }
  })
  return returnArray
}

/**
 * Checks if a given connector is already selected.
 *
 * @param {LocalConnectorData} connector - The connector to check for selection.
 * @return {boolean} True if the connector is selected, false otherwise.
 */
function connectorIsSelected(connector: LocalConnectorData): boolean {
  return !!selectedConnectors.value.find(
    (selectedConnector) =>
      selectedConnector.standard === connector.standard &&
      selectedConnector.format === connector.format,
  )
}

/**
 * Converts ConnectorDetailsData to LocalConnectorData.
 *
 * @param {ConnectorDetailsData} connectorData - The connector data to be converted.
 * @return {LocalConnectorData} The converted local connector data.
 */
function convertConnectorDataToLocalConnectorData(
  connectorData: ConnectorDetailsData,
): LocalConnectorData {
  return {
    displayName: connectorData.displayName,
    format: 'CABLE',
    standard: connectorData.standard,
    iconURL: getSrc(connectorData.iconURL, 'cable'),
  }
}

/**
 * Returns the target connectors icon asset url if one exist
 *
 * @param iconUrlData the 'ConnectorDetailsData' `iconUrl` property.
 * @param format the target format
 * @returns complete file path if it exist or undefined if not.
 */
function getSrc(
  iconUrlData: string | ConnectorDetailsIconData | undefined,
  format: 'cable' | 'socket',
): string | undefined {
  let partialFilePath: string | undefined = undefined
  if (!iconUrlData) return undefined
  if (typeof iconUrlData === 'string') partialFilePath = iconUrlData
  if (typeof iconUrlData === 'object') {
    if (Object.hasOwn(iconUrlData, format)) partialFilePath = iconUrlData[format]
  }
  if (!partialFilePath) return undefined
  return getAssetSrc(partialFilePath)
}

/** List of all possible hard point connectors */
const connectors = computed(() => {
  // convert connector data to locally used connector data objs and order selected connectors at the start of the list.
  return orderConnectors(
    fuelType.value === 'Hydrogen' && connectorDetailsDataMap.get('X_PHONY_HYDROGEN')
      ? [convertConnectorDataToLocalConnectorData(connectorDetailsDataMap.get('X_PHONY_HYDROGEN')!)]
      : getAllTetheredConnectors().map((connector) =>
          convertConnectorDataToLocalConnectorData(connector),
        ),
  )
})
/** List of all possible cables */
const cables = computed(() => {
  // convert connector data to locally used connector data objs and order selected connectors at the start of the list.
  return orderConnectors(
    getAllSocketedConnectors().map((connector) =>
      convertConnectorDataToLocalConnectorData(connector),
    ),
  )
})

// Display helpers

/**
 * Calculates the battery age based on the state of health (SoH).
 *
 * @return {string} A message describing the battery age.
 */
function batteryAgeMessage(): string {
  const degradedPercentage = 100 - SoH.value
  if (degradedPercentage >= 70) return 'this looks like a poorly functioning battery'
  const yearsOld = Math.floor(degradedPercentage / 3)
  if (yearsOld <= 0) return 'equivalent to a new battery'
  return 'equivalent to a ' + yearsOld + ' year old battery with normal usage'
}

/**
 * Returns the source URL of the image associated with the vehicle.
 *
 * @return {string} The source URL of the image.
 */
function imageSrc(): string {
  if (vehicle) {
    // get uploaded image to car in first instance.
    const images = vehicle.imageSrcPaths
    if (images[0]) return images[0]
    // get model image in second instance.
    if (vehicle.evModel)
      return vehicle.evModel?.imageSrc ?? getAssetSrc('car_images/placeholder_car.png')
    // get company logo image in third instance.
    const company = store.state.fleet
    if (company && company.logoSrc) {
      return company.logoSrc
    }
  }
  // get default image in fourth instance.
  return getAssetSrc('car_images/placeholder_car.png')
}

// data setup functions

/**
 * Initializes the component's data properties with default values from the vehicle object.
 *
 * @return {void} No return value.
 */
function setInitialValues(): void {
  loading.value = true
  dirty.value = false
  if (vehicle) {
    // weights
    vehicleWeight.value = vehicle.mass
    additionalWeight.value = 0
    // fuel
    fuelType.value = vehicle.fuelType ?? 'Petrol'
    batterySize.value = vehicle.batterySize
    SoH.value = vehicle.stateOfHealth ? vehicle.stateOfHealth * 100 : 100
    // charging
    acRating.value = vehicle.maxElectricPowerAC
    dcRating.value = vehicle.maxElectricPowerDC
    // energy
    energyUsedWhenStopped.value = 0
    energyUsedWhenMoving.value = 0
    // physics profile
    aerodynamics.value = vehicle.dragCoefficient * 100
    tyreDynamics.value = vehicle.rollingResistanceCoefficient * 1000
    regenerativeBraking.value = vehicle.regenerativeBreakingEfficiency * 100
    powerTrain.value = vehicle.powerChainEfficiency * 100

    // get vehicle to update data from orakle
    vehicle.updateVehicleData()
  }
  selectedConnectors.value = getInitialConnectors()
  nextTick(() => {
    loading.value = false
  })
}

/**
 * Retrieves the initial connectors for a vehicle based on its user-selected plugs or EV model.
 *
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects representing the initial connectors.
 */
function getInitialConnectors(): LocalConnectorData[] {
  if (vehicle) {
    if (vehicle.userSelectedPlugs && vehicle.userSelectedPlugs.length)
      return getUserSelectedConnectors(vehicle.userSelectedPlugs)
    if (vehicle.evModel) return getModelConnectors(vehicle.evModel)
  }
  return []
}

/**
 * Converts an array of user selected plugs into an array of LocalConnectorData objects.
 *
 * @param {UserSelectedPlug[]} userSelectedPlugs - An array of user selected plugs to be converted.
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects representing the user selected connectors.
 */
function getUserSelectedConnectors(
  userSelectedPlugs: DirectusUserSelectedPlug[],
): LocalConnectorData[] {
  const tempArray: LocalConnectorData[] = []
  userSelectedPlugs.forEach((connector) => {
    const extraData = connectorDetailsDataMap.get(connector.standard)
    if (extraData) {
      tempArray.push({
        format: connector.format,
        standard: connector.standard,
        displayName: extraData.displayName,
        iconURL: getSrc(extraData.iconURL, connector.format === 'CABLE' ? 'cable' : 'socket'),
      })
    }
  })
  return tempArray
}

/**
 * Returns an array of LocalConnectorData objects representing the model's compatible connectors.
 *
 * @param {EVModel} model - The EV model to retrieve connectors for.
 * @return {LocalConnectorData[]} An array of LocalConnectorData objects.
 */
function getModelConnectors(model: EVModel): LocalConnectorData[] {
  const tempArray: LocalConnectorData[] = []
  model.compatibleConnectors.forEach((connector) => {
    const extraData = connectorDetailsDataMap.get(connector.standard)
    const alreadyAdded = !!tempArray.find((dataItem) => dataItem.standard === connector.standard)
    if (extraData && !alreadyAdded) {
      tempArray.push({
        format: 'CABLE',
        standard: connector.standard,
        displayName: extraData.displayName,
        iconURL: getSrc(extraData.iconURL, 'cable'),
      })
    }
  })
  return tempArray
}

/**
 * Returns the fallback image source URL.
 *
 * @return {string} The fallback image source URL.
 */
function fallbackImgSrc(): string {
  return getAssetSrc('plug_images/Empty-state-plug-02.svg')
}

// control functions

/**
 * Flags the component's data as dirty, indicating that it has been modified.
 *
 * @return {void} No return value.
 */
function flagAsDirty(): void {
  if (loading.value) return
  dirty.value = true
}

// event handlers

/**
 * Handles the selection of a vehicle by dispatching the selectVehicle action.
 *
 * @return {void} This function does not return a value.
 */
function handleSelect(): void {
  store.dispatch(ActionTypes.selectVehicle, vehicle)
}

/**
 * Handles the selection of the "Other" option in the vehicle details dialog.
 *
 * @return {void} This function does not return a value.
 */
function handleSelectOther(): void {
  store.commit(MutationTypes.setMainDialogContent, MainDialogContent.VEHICLE_GALLERY)
}

onMounted(async () => {
  await nextTick()
  setInitialValues()
})

watch(
  () => vehicle,
  () => {
    setInitialValues()
  },
)

function viewOnMap() {
  if (!vehicle) return
  store.dispatch(ActionTypes.showFleetVehicle, vehicle)
}
</script>

<style scoped>
* :deep(.v-slider--horizontal) {
  margin-left: unset;
  margin-right: unset;
}

* :deep(.v-slider__track-fill) {
  border-radius: 2px; /* override default slider border-radius */
}

* :deep(.v-slider__track-background) {
  border-radius: 2px; /* override default slider border-radius */
}

.svg_base {
  width: 56px;
  height: 56px;
}

.svg_selected {
  transform: scale(1.2);
}

.svg_unselected {
  opacity: 0.6;
}
</style>
